import type { PrefetchContext, PrefetchedDataProps } from 'src/shared/prefetch';

import { useCurrentUserId } from 'src/app/shared/hooks/session';
import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';

import { prefetchStarterDashboardCommentsDataFromApi, useStarterDashboardCommentsDataFromApi } from './pageData';
import {
	prefetchStarterDashboardCommentsPaginatedDataFromApi,
	useStarterDashboardCommentsPaginatedDataFromApi,
} from './pagePaginatedData';

export type StarterDashboardCommentsPrefetchedContext = Readonly<{
	pageData: Parameters<typeof useStarterDashboardCommentsDataFromApi>[0];
	pagePaginatedData: Parameters<typeof useStarterDashboardCommentsPaginatedDataFromApi>[0];
}>;

export type StarterDashboardCommentsPageCombinedState = MergedState<
	ReturnType<typeof useStarterDashboardCommentsDataFromApi>,
	ReturnType<typeof useStarterDashboardCommentsPaginatedDataFromApi>
>;

export function usePageContext(
	slug: string,
	prefetchedData?: StarterDashboardCommentsPrefetchedContext,
): StarterDashboardCommentsPageCombinedState {
	const userId = useCurrentUserId();
	return useMergedStates(
		useStarterDashboardCommentsDataFromApi(prefetchedData?.pageData, slug, userId),
		useStarterDashboardCommentsPaginatedDataFromApi(prefetchedData?.pagePaginatedData, slug),
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<StarterDashboardCommentsPrefetchedContext> {
	const userId = context.session?.user?.id;
	const [pageData, pagePaginatedData] = await Promise.all([
		prefetchStarterDashboardCommentsDataFromApi(context, slug, userId),
		prefetchStarterDashboardCommentsPaginatedDataFromApi(context, slug),
	]);

	return { pageData, pagePaginatedData };
}

export type StarterDashboardCommentsPagePrefetchedDataProps =
	PrefetchedDataProps<StarterDashboardCommentsPrefetchedContext>;
